import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AdminLayout from "./components/admin/AdminLayout";
import Navbar from "./components/Navbar";
import HeroCarousel from "./components/HeroCarousel";
import CategoriesGrid from "./components/CategoriesGrid";
import StihlTips from "./components/StihlTips";
import FinalCTA from "./components/FinalCTA";
import GoogleAnalytics from "./components/shared/GoogleAnalytics";
import ScrollToTop from "./components/shared/ScrollToTop";
import WhatsAppButton from "./components/shared/WhatsAppButton";
import VideosPage from "./pages/VideosPage";
import ProtectedRoute from "./components/shared/ProtectedRoute";
import Footer from "./components/Footer";
import About from "./pages/About";
import Contact from "./pages/Contact";
import ContactSuccess from "./pages/ContactSuccess";
import NotFound from "./pages/NotFound";
import ProductsPage from "./pages/ProductsPage";
import SingleProduct from "./pages/SingleProduct";
import Login from "./pages/Login";

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminBrands = lazy(() => import("./pages/AdminBrands"));
const ProductForm = lazy(() => import("./pages/ProductForm"));
const StockControl = lazy(() => import("./pages/StockControl"));
const AdminInquiries = lazy(() => import("./pages/AdminInquiries"));
const AdminVideos = lazy(() => import("./pages/AdminVideos"));

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

const HomePage = () => (
  <div className="home-page">
    <Helmet>
      <title>Inicio | Cáceres Maquinarias</title>
    </Helmet>
    <HeroCarousel />
    <CategoriesGrid />
    <StihlTips />
    <FinalCTA />
  </div>
);

const ConditionalWhatsApp = () => {
  const location = useLocation();
  if (
    location.pathname.startsWith("/admin") ||
    location.pathname === "/login"
  ) {
    return null;
  }
  return <WhatsAppButton />;
};

const AdminLoading = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f4f4f4",
    }}
  >
    <div className="loading-spinner" />
  </div>
);

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Helmet>
          <title>
            Cáceres Maquinarias | Concesionario Oficial STIHL en Casilda
          </title>
          <meta
            name="description"
            content="Concesionario oficial STIHL en Casilda, Santa Fe. Motosierras, cortadoras de césped, motoguadañas, sopladores, hidrolavadoras y más. Desde 1977 brindando calidad profesional."
          />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Cáceres Maquinarias" />
          <meta
            property="og:title"
            content="Cáceres Maquinarias | Concesionario Oficial STIHL en Casilda"
          />
          <meta
            property="og:description"
            content="Concesionario oficial STIHL en Casilda, Santa Fe. Motosierras, cortadoras de césped, motoguadañas, sopladores, hidrolavadoras y más."
          />
          <meta
            property="og:url"
            content="https://www.caceresmaquinarias.com.ar/"
          />
          <meta
            property="og:image"
            content="https://www.caceresmaquinarias.com.ar/Logo-CM.png"
          />
          <meta name="twitter:card" content="summary_large_image" />
        </Helmet>
        <GoogleAnalytics />
        <ScrollToTop />
        <Routes>
          <Route
            path="/"
            element={
              <PublicLayout>
                <HomePage />
              </PublicLayout>
            }
          />
          <Route
            path="/videos"
            element={
              <PublicLayout>
                <VideosPage />
              </PublicLayout>
            }
          />
          <Route
            path="/nosotros"
            element={
              <PublicLayout>
                <About />
              </PublicLayout>
            }
          />
          <Route
            path="/contacto"
            element={
              <PublicLayout>
                <Contact />
              </PublicLayout>
            }
          />
          <Route
            path="/consulta-enviada"
            element={
              <PublicLayout>
                <ContactSuccess />
              </PublicLayout>
            }
          />
          <Route
            path="/productos"
            element={
              <PublicLayout>
                <ProductsPage />
              </PublicLayout>
            }
          />
          <Route
            path="/productos/:productSlug"
            element={
              <PublicLayout>
                <SingleProduct />
              </PublicLayout>
            }
          />

          <Route path="/login" element={<Login />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <AdminDashboard />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/productos"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <AdminProducts />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/productos/crear"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <ProductForm />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/productos/editar/:id"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <ProductForm />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/categorias"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <AdminCategories />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/marcas"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <AdminBrands />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stock"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <StockControl />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultas"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <AdminInquiries />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/videos"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Suspense fallback={<AdminLoading />}>
                    <AdminVideos />
                  </Suspense>
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <PublicLayout>
                <NotFound />
              </PublicLayout>
            }
          />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <ConditionalWhatsApp />
      </Router>
    </AuthProvider>
  );
};

export default App;
